import { useDroppable } from "@dnd-kit/core"
import { Dayjs } from "dayjs"
import { DateRangeViewEvent, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import { EventDisplayInfo } from "../scheduleModel"
import MonthViewEvent from "./MonthViewEvent"
import MonthViewConsultation from "./MonthViewConsultation"
import MonthViewTask from "./MonthViewTask"
import OptimizeButton from "../OptimizeSchedule/OptimizeButton"

export type Props = {
  date: Dayjs
  events: DateRangeViewEvent[]
  consultations?: ConsultationCalendarEvent[]
  tasks?: TaskCalendarEvent[]
  hasEvents: boolean
  eventDisplayInfo: Map<number, EventDisplayInfo>
  isCurrentMonth: boolean
  isSelected: boolean
  onDayClick: (date: Dayjs) => void
  onEventClick: (event: DateRangeViewEvent) => void
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onTaskClick?: (event: TaskCalendarEvent) => void
  onDayScheduleClick: (date: Dayjs) => void
  onOpenOptimize: (date: Dayjs) => void
  reservedTopHeight?: number
}

type CalendarItem =
  | { type: 'event' } & DateRangeViewEvent
  | { type: 'consultation' } & ConsultationCalendarEvent
  | { type: 'task' } & TaskCalendarEvent

const MonthViewDay = ({ date, events, consultations = [], tasks = [], hasEvents, eventDisplayInfo, isCurrentMonth, isSelected, onDayClick, onEventClick, onConsultationClick, onTaskClick, onDayScheduleClick, onOpenOptimize, reservedTopHeight = 0 }: Props) => {
  const showOptimizeButton = hasEvents
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${date.format('YYYY-MM-DD')}`,
    data: { date },
  })

  const sortedItems: CalendarItem[] = [
    ...events.map((e): CalendarItem => ({ type: 'event', ...e })),
    ...consultations.map((c): CalendarItem => ({ type: 'consultation', ...c })),
    ...tasks.map((e): CalendarItem => ({ type: 'task', ...e })),
  ].sort((a, b) => {
    if (a.start === b.start) {
      return 0
    }
    return a.start > b.start ? 1 : -1
  })

  const getBackgroundColor = () => {
    if (isOver) {
      return '#e6f7ff'
    }
    return isCurrentMonth ? '#fff' : '#fafafa'
  }

  return (
    <div
      ref={setNodeRef}
      onClick={() => onDayScheduleClick(date)}
      style={{
        flex: 1,
        minWidth: 0,
        borderRight: '1px solid #e8e8e8',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: getBackgroundColor(),
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: showOptimizeButton ? 'space-between' : 'flex-end',
          alignItems: 'center',
          height: 40,
          padding: '0 8px',
        }}
      >
        {showOptimizeButton && <OptimizeButton onClick={() => onOpenOptimize(date)} />}
        <div
          onClick={(e) => {
            e.stopPropagation()
            onDayClick(date)
          }}
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isSelected ? '#FF5C35' : 'transparent',
            color: isSelected ? '#fff' : isCurrentMonth ? '#333' : '#bbb',
            fontWeight: isSelected ? 600 : 400,
            fontSize: 14,
          }}
        >
          {date.date()}
        </div>
      </div>

      <div
        style={{
          padding: '0 4px 8px 4px',
          paddingTop: reservedTopHeight > 0 ? reservedTopHeight + 2 : 0,
        }}
      >
        {sortedItems.map((item) => {
          if (item.type === 'event') {
            return <MonthViewEvent key={`event-${item.id}`} event={item} displayInfo={eventDisplayInfo.get(item.id)} onEventClick={onEventClick} />
          }
          if (item.type === 'task') {
            return <MonthViewTask key={`task-${item.id}`} event={item} onTaskClick={onTaskClick} />
          }
          if (item.type === 'consultation') {
            return <MonthViewConsultation key={`consultation-${item.id}`} consultation={item} onConsultationClick={onConsultationClick} />
          }
          return null
        })}
      </div>
    </div>
  )
}

export default MonthViewDay
